// Live, staff-editable Gold-desk settings (buy/sell rates, min/max amounts,
// available stock, per-side enable toggles). Stored as a single row in Supabase
// (gold_settings) and read publicly at page load — an edit shows up on the live
// Gold page within ~1s, no rebuild. The server re-reads the same row to bill
// authoritatively (see bff/_shared/goldSettings.ts), so display and billing agree.

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/features/auth/supabaseClient';
import {
  GOLD_BUY_MIN_M,
  GOLD_BUY_RATE_USD_PER_M,
  GOLD_MAX_M,
  GOLD_SELL_MIN_M,
  GOLD_SELL_RATE_USD_PER_M,
  isSupabaseConfigured,
} from '@/lib/constants';

export interface GoldSettings {
  buyRate: number; // USD per 1M, buying
  sellRate: number; // USD per 1M, selling
  buyMin: number; // min buy (M)
  sellMin: number; // min sell (M)
  maxM: number; // max order size (M)
  stockM: number; // available gold for buyers (M); 0 = untracked / unlimited
  buyEnabled: boolean;
  sellEnabled: boolean;
}

/** Built-in defaults (mirror the pricing constants) used until settings load. */
export function goldDefaults(): GoldSettings {
  return {
    buyRate: GOLD_BUY_RATE_USD_PER_M,
    sellRate: GOLD_SELL_RATE_USD_PER_M,
    buyMin: GOLD_BUY_MIN_M,
    sellMin: GOLD_SELL_MIN_M,
    maxM: GOLD_MAX_M,
    stockM: 0,
    buyEnabled: true,
    sellEnabled: true,
  };
}

const live = () => isSupabaseConfigured && supabase;
const num = (v: unknown, fb: number) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : fb;
};

// Module-level cache of the latest loaded settings. The synchronous client
// preview pricer (previewPrice.ts / quoteClient) has no hook access, so it reads
// this so the cart estimate matches the live Gold page. Warmed app-wide by the
// useGoldSettings() call mounted in the Header; falls back to defaults until then.
let currentGold: GoldSettings = goldDefaults();
export function currentGoldSettings(): GoldSettings {
  return currentGold;
}

function rowToSettings(r: Record<string, unknown> | null): GoldSettings {
  const d = goldDefaults();
  if (!r) return d;
  return {
    buyRate: num(r.buy_rate, d.buyRate),
    sellRate: num(r.sell_rate, d.sellRate),
    buyMin: num(r.buy_min, d.buyMin),
    sellMin: num(r.sell_min, d.sellMin),
    maxM: num(r.max_m, d.maxM),
    stockM: num(r.stock_m, d.stockM),
    buyEnabled: r.buy_enabled !== false,
    sellEnabled: r.sell_enabled !== false,
  };
}

/** Live Gold-desk settings, cached by React Query. Always returns a value
 *  (defaults) so the Gold page renders instantly. Admin save invalidates it. */
export function useGoldSettings() {
  return useQuery<GoldSettings>({
    queryKey: ['gold-settings'],
    placeholderData: goldDefaults(),
    staleTime: 30_000,
    queryFn: async () => {
      if (!live()) return goldDefaults();
      const { data, error } = await supabase!
        .from('gold_settings')
        .select('buy_rate, sell_rate, buy_min, sell_min, max_m, stock_m, buy_enabled, sell_enabled')
        .eq('id', 1)
        .maybeSingle();
      if (error) {
        console.warn('[gold-settings] load failed:', error.message);
        return goldDefaults();
      }
      const parsed = rowToSettings(data as Record<string, unknown> | null);
      currentGold = parsed; // keep the sync preview cache fresh
      return parsed;
    },
  });
}

/** Save the Gold-desk settings (staff only, via the SECURITY DEFINER RPC). */
export async function saveGoldSettings(s: GoldSettings): Promise<boolean> {
  if (!live()) return false;
  const { error } = await supabase!.rpc('admin_save_gold_settings', {
    p_buy_rate: s.buyRate,
    p_sell_rate: s.sellRate,
    p_buy_min: s.buyMin,
    p_sell_min: s.sellMin,
    p_max_m: s.maxM,
    p_stock_m: s.stockM,
    p_buy_enabled: s.buyEnabled,
    p_sell_enabled: s.sellEnabled,
  });
  if (error) {
    console.warn('[gold-settings] save failed:', error.message);
    return false;
  }
  return true;
}
